import { useHistory } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import BasePopup from '../../components/BasicComponent/BasePopup'
import BasicCheckBox from '../../components/BasicComponent/BasicCheckBox'
import LoadingScreen from '../../components/BasicComponent/LoadingScreen'
import BaseButton from '../../components/BasicComponent/BaseButton'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import BasePopupTerm from '../../components/BasicComponent/BasePopupTerm'
import LayoutPartner from '../../components/BasicComponent/LayoutPartner'
import AutomatedTrafficFineNotificationAuthenticationInfo from './components/AutomatedTrafficFineNotificationAuthenticationInfo'
import { PATH } from '../../constants/router'
import { APP_USER_IDENTITY_STATUS, useAppUserContext } from '../../context/AppUserContext'
import { parseFromLocalStorage } from '../../helper/localStorage'
import { SDK_SOURCE } from '../../sdk'
import { ViettelMoneyPrivacyPolicyHtml } from '../../sdk/term/ViettelMoney'
import { VIETTEL_MONEY_DEFAULT_ERROR_CODE, VIETTEL_MONEY_DEFAULT_ERROR_MESSAGE, VIETTEL_MONEY_ERROR_MESSAGE } from '../../sdk/ViettelMoney'
import './index.scss'

const termsData = [
  {
    title: 'Chính sách quyền riêng tư',
    intro: [
      {
        content: 'Chúng tôi sử dụng số điện thoại của bạn để phục vụ tra cứu và đăng ký nhận thông báo phạt nguội.'
      },
      {
        content: 'Việc tiếp tục sử dụng dịch vụ đồng nghĩa với việc bạn đã đọc, hiểu và đồng ý với nội dung chính sách này.'
      }
    ]
  },
  {
    title: '1. Thông tin được sử dụng',
    intro: [
      {
        content: 'Thông tin chính được sử dụng trong giai đoạn này là:',
        details: ['Số điện thoại của người dùng.']
      },
      {
        content: 'Ở bước xác nhận này, hệ thống không yêu cầu bạn nhập tay số điện thoại.'
      }
    ]
  },
  {
    title: '2. Mục đích sử dụng',
    intro: [
      {
        content: 'Số điện thoại được dùng để:',
        details: [
          'Xác nhận người dùng đang sử dụng dịch vụ.',
          'Tra cứu thông tin vi phạm theo yêu cầu của người dùng.',
          'Gửi thông báo và hỗ trợ các chức năng liên quan đến dịch vụ.'
        ]
      },
      {
        content: 'Số điện thoại không được sử dụng cho mục đích mua bán hoặc khai thác thương mại trái phép.'
      }
    ]
  },
  {
    title: '3. Chia sẻ và bảo mật',
    intro: [
      {
        content: 'Chúng tôi chỉ chia sẻ thông tin khi thật sự cần thiết để cung cấp dịch vụ hoặc khi có yêu cầu hợp pháp từ cơ quan có thẩm quyền.',
        details: [
          'Không bán số điện thoại cho bên thứ ba.',
          'Áp dụng biện pháp phù hợp để bảo vệ dữ liệu.',
          'Chỉ lưu dữ liệu trong thời gian cần thiết để phục vụ dịch vụ.'
        ]
      },
      {
        content: 'Bạn có thể liên hệ đơn vị cung cấp dịch vụ nếu cần hỗ trợ thêm về dữ liệu cá nhân.'
      }
    ]
  }
]

const buildSdkErrorInfoText = (sdkError) => {
  if (!sdkError) {
    return VIETTEL_MONEY_DEFAULT_ERROR_MESSAGE
  }

  if (sdkError.source === SDK_SOURCE.VIETTEL_MONEY) {
    const code = sdkError.errorCode || VIETTEL_MONEY_DEFAULT_ERROR_CODE
    return VIETTEL_MONEY_ERROR_MESSAGE[code] || VIETTEL_MONEY_DEFAULT_ERROR_MESSAGE
  }

  return VIETTEL_MONEY_DEFAULT_ERROR_MESSAGE
}

export default function TrafficFineCheckConsent() {
  const history = useHistory()
  const {
    userData,
    pendingUserData,
    identityStatus,
    isConsentRequired,
    isLoadingUserData,
    sdkError,
    refreshUserData,
    confirmPendingUserData
  } = useAppUserContext()
  const [confirmTerm, setConfirmTerm] = useState(true)
  const [confirmTermSheetVisible, setConfirmTermSheetVisible] = useState(false)
  const [confirmFailedPopupOpen, setConfirmFailedPopupOpen] = useState(false)

  useEffect(() => {
    if (isLoadingUserData) {
      return
    }

    if (!isConsentRequired && userData?.phoneNumberWithUsernamePrefix) {
      history.replace(PATH.TRAFFIC_FINE_LOOKUP)
    }
  }, [history, isConsentRequired, isLoadingUserData, userData?.phoneNumberWithUsernamePrefix])

  const isMissingPhone = identityStatus === APP_USER_IDENTITY_STATUS.MISSING_PHONE
  const isSdkError = identityStatus === APP_USER_IDENTITY_STATUS.SDK_AUTHENTICATION_ERROR
  const isErrorState = isMissingPhone || isSdkError

  const infoText = useMemo(() => {
    if (isSdkError) {
      return buildSdkErrorInfoText(sdkError)
    }

    if (isMissingPhone) {
      return 'Không nhận được thông tin số điện thoại. Vui lòng thử lại.'
    }

    if (identityStatus === APP_USER_IDENTITY_STATUS.CONSENT_CHANGED) {
      return 'Các dữ liệu sau sẽ được chia sẻ, xử lý với hệ thống tra cứu và thông báo phạt nguội:'
    }

    return 'Các dữ liệu sau sẽ được chia sẻ, xử lý với hệ thống tra cứu và thông báo phạt nguội:'
  }, [identityStatus, isMissingPhone, isSdkError, sdkError])

  const shouldShowSplashLoading = isLoadingUserData || (!isConsentRequired && !!userData?.phoneNumberWithUsernamePrefix)

  if (shouldShowSplashLoading) {
    return <LoadingScreen open solid />
  }

  const handleNextStep = async () => {
    if (!isErrorState && typeof navigator !== 'undefined' && navigator.onLine === false) {
      setConfirmFailedPopupOpen(true)
      return
    }

    if (isErrorState) {
      await refreshUserData()
      return
    }

    const isConfirmed = confirmPendingUserData()

    if (isConfirmed) {
      history.replace(PATH.TRAFFIC_FINE_LOOKUP)
    }
  }

  return (
    <LayoutPartner headerTitle="Xác nhận thông tin">

      <div className="TrafficFineCheckConsent">
        <AutomatedTrafficFineNotificationAuthenticationInfo description={infoText} isError={isErrorState} />
      </div>

      <FixedBottom elementPaddingBottom="LayoutPartner">
        {!isErrorState && (
          <div style={{ marginBottom: '12px' }}>
            <BasicCheckBox checked={confirmTerm} onChange={(e) => setConfirmTerm(e.target.checked)}>
              <span>
                Tôi đã đọc Mục đích chia sẻ, xử lý dữ liệu,{' '}
                <span
                  style={{ color: 'var(--brand-primary)' }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setConfirmTermSheetVisible(true)
                  }}>
                  Quyền, nghĩa vụ của chủ thể dữ liệu{' '}
                </span>
                và đồng ý chia sẻ, xử lý dữ liệu cá nhân.
              </span>
            </BasicCheckBox>
          </div>
        )}

        <BaseButton
          disabled={isLoadingUserData || (!isErrorState && (!confirmTerm || !pendingUserData?.phoneNumberWithUsernamePrefix))}
          onClick={handleNextStep}>
          {isErrorState ? 'Thử lại' : 'Xác nhận'}
        </BaseButton>
      </FixedBottom>

      <BasePopupTerm
        isOpen={confirmTermSheetVisible}
        onClose={() => setConfirmTermSheetVisible(false)}
        title="Chi tiết"
        textButton="Đóng"
        onConfirm={() => setConfirmTermSheetVisible(false)}>
        {parseFromLocalStorage(process.env.REACT_APP_MINIAPP_VIETTEL_MONEY) ? (
          <div dangerouslySetInnerHTML={{ __html: ViettelMoneyPrivacyPolicyHtml }} />
        ) : (
          <div className="TrafficFineCheckConsent_terms">
            {termsData.map((item, index) => (
              <div key={index} className="terms_item">
                <div className="terms_item_title">{item.title}</div>
                {item.intro.map((intro, introIndex) => (
                  <div key={introIndex}>
                    <div className="terms_item_intro">
                      <div className="terms_item_intro_content">{intro.content}</div>
                      <div className="terms_item_intro_details">
                        {intro.details && intro.details.map((detail, detailIndex) => <p key={detailIndex}>{detail}</p>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </BasePopupTerm>
      <BasePopup
        open={confirmFailedPopupOpen}
        onClose={() => setConfirmFailedPopupOpen(false)}
        title="Xác thực thất bại"
        content={
          <prev>
            Không thể xác thực số điện thoại.
            <br /> Vui lòng thử lại.
          </prev>
        }
      />
    </LayoutPartner>
  )
}
